import axios from 'axios';

// Get environment and determine appropriate API URL
const getApiUrl = () => {
  // Use environment variable if provided (from Cloud Run or .env files)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // For local development without env vars, use environment-specific defaults
  const env = process.env.REACT_APP_ENV || 'development';
  
  switch (env) {
    case 'production':
      return 'https://api.prefab-hakiki.id/api';
    case 'staging':
      return 'https://api-staging.prefab-hakiki.id/api';
    case 'development':
      return 'https://api-dev.prefab-hakiki.id/api';
    default:
      // Local development fallback
      return 'http://localhost:3000/api';
  }
};

// Ensure we don't have duplicate /api prefixes
const API_URL = getApiUrl();

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  // Tanpa timeout, request yang menggantung membuat state `loading` di halaman
  // (mis. Update Progress) stuck true → seluruh form disabled tanpa cara pulih.
  // 120s masih longgar untuk upload lampiran di koneksi lapangan yang lambat.
  timeout: 120000,
  timeoutErrorMessage: 'Permintaan ke server melebihi batas waktu. Periksa koneksi lalu coba lagi.',
  headers: {
    'Content-Type': 'application/json',
  },
  // Penting: Aktifkan withCredentials untuk mendukung cookies
  withCredentials: true,
});

// Add a log to help debug requests in development
if (process.env.NODE_ENV === 'development') {
  api.interceptors.request.use(request => {
    console.log('API Request URL:', request.baseURL + request.url);
    return request;
  });
  
  // Add response logging for debugging
  api.interceptors.response.use(
    response => {
      console.log('API Response:', {
        url: response.config.url,
        status: response.status,
        data: response.data
      });
      return response;
    },
    error => {
      console.error('API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data
      });
      return Promise.reject(error);
    }
  );
}

// Add request interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Single, app-wide handler for an expired/invalid session. Idempotent and
// guarded so concurrent 401s (multiple in-flight requests) trigger exactly one
// logout + redirect. Clears auth, remembers where the user was so we can send
// them back after re-login, and notifies React (AuthContext) before navigating.
let isHandlingUnauthorized = false;
export const handleUnauthorized = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');

  // Let the React tree react within the SPA (clears user → route guard redirect).
  try {
    window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'session-expired' } }));
  } catch (e) { /* non-browser env */ }

  if (window.location.pathname === '/login' || isHandlingUnauthorized) return;
  isHandlingUnauthorized = true;

  // Remember the intended destination for a post-login redirect.
  try {
    const { pathname, search, hash } = window.location;
    sessionStorage.setItem('postLoginRedirect', `${pathname}${search}${hash}`);
  } catch (e) { /* storage unavailable */ }

  // Hard redirect guarantees a clean slate regardless of which HTTP client
  // (api instance, raw axios, or fetch) surfaced the 401.
  window.location.replace('/login');
};

// 401 response interceptor — shared by BOTH the `api` instance and the global
// axios default, because several pages call `axios.get('/api/...')` directly
// (bypassing the `api` instance). Without covering the default instance those
// pages silently rendered empty data on an expired session with no redirect.
const unauthorizedResponseInterceptor = (error) => {
  if (error.response && error.response.status === 401) {
    console.warn('Authentication error: session expired or invalid');
    handleUnauthorized();
  }
  return Promise.reject(error);
};

api.interceptors.response.use((response) => response, unauthorizedResponseInterceptor);
axios.interceptors.response.use((response) => response, unauthorizedResponseInterceptor);

export default api;